/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';

import type {
  UniversityProgramDegreeDescriptionPagePathwaySectionLinks as PathwaysLinksType,
  UniversityProgramDegreeDescriptionPagePathwaySection as PathwaysType,
  UniversityProgramPathwayBlock,
} from 'bundles/degree-description/__generated__/baseContentfulTypes';
import PathwayBlock, {
  PathwayBlockProductCard,
} from 'bundles/degree-description/components/modules/pathways/PathwayBlock';
import Container from 'bundles/degree-description/components/shared/Container';
import {
  filterRichTextContentByNodeType,
  isEmptyChild,
  renderRichText,
} from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';

type PathwaySectionProps = {
  pathwaysText: PathwaysType['json'];
  pathwayBlocks: PathwaysLinksType;
};

const styles = {
  container: () => css`
    padding-top: var(--cds-spacing-600);
    padding-bottom: var(--cds-spacing-600);
    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-300);
      padding-bottom: var(--cds-spacing-300);
    }
  `,
  marginBotton16: css`
    margin-bottom: 16px;
  `,
  marginBottom24: css`
    margin-bottom: 24px;
  `,
  marginBottom32: css`
    margin-bottom: 32px;
  `,
};

const getRichTextRenderOptions = (links: Partial<PathwaysLinksType>): Options => {
  return {
    renderNode: {
      [BLOCKS.HEADING_3]: (_, children) => {
        const theme = useDegreeDescriptionPageTheme();
        if (isEmptyChild(children)) return null;
        return (
          <Typography
            variant="h1semibold"
            component="h3"
            css={[styles.marginBottom24, theme.mobileFontSizeStyle?.header]}
          >
            {children}
          </Typography>
        );
      },
      [BLOCKS.PARAGRAPH]: (_, children) => {
        const theme = useDegreeDescriptionPageTheme();
        if (isEmptyChild(children)) return null;
        return (
          <Typography2
            variant="bodyPrimary"
            component="div"
            css={[styles.marginBottom32, theme.mobileFontSizeStyle?.body]}
          >
            {children}
          </Typography2>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const theme = useDegreeDescriptionPageTheme();
        // Only Pathway items are rendered from here
        const id = node?.data?.target?.sys?.id;
        const entry = links?.entries?.block.find((link) => link?.sys?.id === id) as UniversityProgramPathwayBlock;
        if (entry && entry?.__typename === 'UniversityProgramPathwayBlock') {
          return (
            <PathwayBlock
              title={
                <Typography
                  variant="h2semibold"
                  component="h4"
                  css={[styles.marginBotton16, theme.mobileFontSizeStyle?.subHeader]}
                >
                  {entry.title}
                </Typography>
              }
              productCards={entry?.programListCollection?.items?.map((productCard) => {
                return (
                  <PathwayBlockProductCard
                    key={productCard?.sys?.id}
                    title={productCard?.title}
                    productType={productCard?.productType}
                    courseLength={productCard?.courseLength}
                    productUrl={productCard?.productUrl}
                  />
                );
              })}
            />
          );
        }
        return null;
      },
    },
  };
};

const PathwaysModule = ({ pathwaysText, pathwayBlocks }: PathwaySectionProps) => {
  const richTextRenderOptions = getRichTextRenderOptions(pathwayBlocks);
  return (
    <div>
      <Container css={styles.container}>
        {renderRichText({
          content: filterRichTextContentByNodeType(pathwaysText, [
            BLOCKS.HEADING_3,
            BLOCKS.PARAGRAPH,
            BLOCKS.EMBEDDED_ENTRY,
          ]),
          richTextRenderOptions,
          linkTrackingName: 'pathways_module_link',
        })}
      </Container>
    </div>
  );
};

export default PathwaysModule;
